/**
 * Preferences.
 *
 * Kept in `localStorage` rather than anywhere clever: there is no account, no
 * sync and no server, and there never will be. Unknown keys from a newer build
 * are preserved on write so that moving between versions does not silently
 * discard someone's choices.
 */
import type { QualityName } from './quality';

export type Style = 'hand' | 'free';

export interface Settings {
  quality: QualityName;
  /**
   * How the photograph is taken. `hand` is the mirror selfie everyone already
   * takes; `free` props the phone up and uses the timer, which is the one way
   * to get a picture with no phone in it to remove at all.
   */
  style: Style;
  /** Seconds of countdown in hands-free mode. */
  timer: 3 | 5 | 10;
  /** In hands-free mode, raising a hand above the head starts the countdown. */
  raiseToShoot: boolean;
  autoLevel: boolean;
  unmirror: boolean;
  focalPlane: boolean;
  watermark: boolean;
  format: 'image/jpeg' | 'image/png' | 'image/webp';
  quality01: number;
  haptics: boolean;
  shutterSound: boolean;
  /** Keep finished photographs in the on-device gallery. */
  keepGallery: boolean;
  theme: 'system' | 'light' | 'dark';
  /** Cleared once the walkthrough has been seen. */
  onboarded: boolean;
}

export const DEFAULTS: Settings = {
  quality: 'balanced',
  style: 'hand',
  timer: 5,
  raiseToShoot: true,
  autoLevel: true,
  unmirror: true,
  focalPlane: false,
  watermark: false,
  format: 'image/jpeg',
  quality01: 0.94,
  haptics: true,
  shutterSound: false,
  keepGallery: true,
  theme: 'system',
  onboarded: false,
};

const KEY = 'second-person:settings:v1';

type Listener = (settings: Settings) => void;

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    // Private-mode Safari throws on read as well as write. Defaults are fine.
    return { ...DEFAULTS };
  }
}

export class SettingsStore {
  #value: Settings = read();
  #listeners = new Set<Listener>();

  get value(): Readonly<Settings> {
    return this.#value;
  }

  get<K extends keyof Settings>(key: K): Settings[K] {
    return this.#value[key];
  }

  set<K extends keyof Settings>(key: K, value: Settings[K]): void {
    if (this.#value[key] === value) return;
    this.#value = { ...this.#value, [key]: value };
    this.#persist();
    this.#emit();
  }

  patch(values: Partial<Settings>): void {
    this.#value = { ...this.#value, ...values };
    this.#persist();
    this.#emit();
  }

  reset(): void {
    this.#value = { ...DEFAULTS, onboarded: this.#value.onboarded };
    this.#persist();
    this.#emit();
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(): void {
    for (const l of this.#listeners) l(this.#value);
  }

  #persist(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.#value));
    } catch {
      // Storage being unavailable is not worth interrupting a capture over.
    }
  }
}
